<template>
  <div class="container py-4">
    <div class="page-card">
      <div
        class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
      >
        <h4 class="mb-0">Sales</h4>
        <div class="d-flex gap-2 align-items-center">
          <b-input-group>
            <b-input-group-prepend is-text>
              <i class="fas fa-filter akkurate-green"></i>
            </b-input-group-prepend>
            <b-form-select
              class="akkurate_input filter-list filter-list-choice status-filter form-select"
              v-model="statusFilter"
              :options="statusOptions"
              @change="load"
            />
          </b-input-group>
          <b-form-select
            v-if="canAuditFloor"
            v-model="cashierFilter"
            :options="cashierOptions"
            class="akkurate_input filter-list filter-list-choice status-filter form-select"
            @change="load"
          />
          <b-form-select
            v-model="paymentFilter"
            :options="paymentOptions"
            class="akkurate_input filter-list filter-list-choice status-filter form-select"
            @change="load"
          />
          <icon-button
            :func="() => load()"
            :icon="'rotate-right'"
            :title="'Refresh'"
            :classes="'akkurate-green-btn akkurate-small'"
            :btnName="''"
          />
        </div>
      </div>

      <div
        v-if="heldBanner.count"
        class="sales-held-banner d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
      >
        <span>
          <strong>{{ heldBanner.count }}</strong>
          held {{ heldBanner.count === 1 ? "sale" : "sales" }}
          {{ canAuditFloor ? "on this floor" : "(yours)" }}
          <span v-if="canAuditFloor && heldBanner.others">
            · {{ heldBanner.others }} by other cashiers
          </span>
        </span>
        <button
          type="button"
          class="btn akkurate-link-btn"
          @click="showHeldOnly"
        >
          View held
        </button>
      </div>

      <b-table
        :items="sales"
        :fields="tableFields"
        :busy="loading"
        responsive
        hover
        small
        :tbody-tr-class="rowClass"
      >
        <template #cell(userName)="data">
          {{ data.value }}
          <span
            v-if="isMine(data.item)"
            class="badge bg-light text-dark border ms-1"
            >You</span
          >
        </template>
        <template #cell(paymentMethods)="data">
          {{ formatPayments(data.item) }}
        </template>
        <template #cell(holdLabel)="data">
          {{ data.value || (data.item.status === "held" ? "—" : "") }}
        </template>
        <template #cell(total)="data">
          {{ formatMoney(data.value) }}
        </template>
        <template #cell(status)="data">
          <b-badge :variant="payStatusBadge(data.value)">{{
            data.value?.toUpperCase()
          }}</b-badge>
        </template>
        <template #cell(createdAt)="data">
          {{ formatDateAndTime(data.value) }}
        </template>
        <template #cell(actions)="data">
          <icon-button
            v-if="canReprint(data.item.status)"
            :func="() => viewReceipt(data.item.id)"
            :icon="'receipt'"
            :title="reprintTitle(data.item.status)"
            :classes="'akkurate-gray-btn btn-sm'"
            :btnName="''"
          />

          <icon-button
            v-if="data.item.status === 'held'"
            :func="() => recallOnPos(data.item.id)"
            :icon="'cart-plus'"
            :title="'Recall on POS'"
            :classes="'akkurate-green-btn btn-sm'"
            :btnName="''"
          />

          <icon-button
            v-if="data.item.status === 'completed' && canRefund"
            :func="() => voidSale(data.item.id)"
            :icon="'ban'"
            :title="'Void'"
            :classes="'akkurate-danger-bg btn-sm'"
            :btnName="''"
          />

          <icon-button
            v-if="data.item.status === 'completed' && canRefund"
            :func="() => refundSale(data.item.id)"
            :icon="'rotate-left'"
            :title="'Refund'"
            :classes="'akkurate-orange btn-sm'"
            :btnName="''"
          />

          <icon-button
            v-if="data.item.status === 'pending' || data.item.status === 'held'"
            :func="() => cancelSale(data.item.id)"
            :icon="'times'"
            :title="'Cancel'"
            :classes="'akkurate-danger-bg btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="showReceipt"
      :title="receiptModalTitle"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <div v-if="receiptHtml" class="receipt-preview" v-html="receiptHtml" />
      <template #modal-footer="{ ok }">
        <icon-button
          :func="() => printReceipt()"
          :icon="'print'"
          :title="''"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Print'"
        />

        <icon-button
          :func="() => ok()"
          :icon="'times'"
          :title="''"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Close'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";
import Multiselect from "vue-multiselect";
import { icon } from "@fortawesome/fontawesome-svg-core";

export default {
  name: "SalesHistory",
  components: {
    IconButton,
    Multiselect,
  },
  data() {
    return {
      sales: [],
      heldSales: [],
      loading: false,
      statusFilter: "",
      cashierFilter: "",
      paymentFilter: "",
      cashierOptions: [{ value: "", text: "All cashiers" }],
      showReceipt: false,
      receiptHtml: "",
      receiptStatus: "",
      paymentOptions: [
        { value: "", text: "All payments" },
        { value: "Cash", text: "Cash" },
        { value: "MobileMoney", text: "Mobile money" },
        { value: "Card", text: "Card" },
      ],
      statusOptions: [
        { value: "", text: "All statuses" },
        { value: "completed", text: "Completed" },
        { value: "held", text: "Held" },
        { value: "pending", text: "Pending" },
        { value: "voided", text: "Voided" },
        { value: "refunded", text: "Refunded" },
      ],
      fields: [
        { key: "saleNumber", label: "Sale #" },
        { key: "userName", label: "Cashier" },
        { key: "holdLabel", label: "Hold tip" },
        { key: "paymentMethods", label: "Payment" },
        { key: "total", label: "Total" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Date" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId", "hasPermission", "currentUser"]),
    canRefund() {
      return this.hasPermission("REFUND");
    },
    canAuditFloor() {
      return this.hasPermission("SALES_AUDIT");
    },
    tableFields() {
      if (this.canAuditFloor) return this.fields;
      return this.fields.filter((f) => f.key !== "userName");
    },
    receiptModalTitle() {
      if (this.receiptStatus === "held") return "Held receipt (not paid)";
      if (this.receiptStatus === "pending") return "Pending receipt (not paid)";
      return "Receipt";
    },
    heldBanner() {
      const mineId = this.currentUser?.id;
      const others = this.heldSales.filter(
        (s) => s.userId && s.userId !== mineId,
      ).length;
      return { count: this.heldSales.length, others };
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
    "$route.query"() {
      this.applyRouteFilters();
      this.load();
    },
  },
  mounted() {
    this.applyRouteFilters();
    this.load();
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString();
    },
    statusVariant(status) {
      if (status === "completed") return "success";
      if (status === "pending") return "warning";
      if (status === "voided") return "secondary";
      if (status === "refunded") return "danger";
      return "secondary";
    },
    applyRouteFilters() {
      const q = this.$route.query || {};
      if (q.status !== undefined) this.statusFilter = q.status || "";
      if (this.canAuditFloor && q.userId !== undefined) {
        this.cashierFilter = q.userId || "";
      } else if (!this.canAuditFloor) {
        this.cashierFilter = "";
      }
      if (q.paymentMethod !== undefined)
        this.paymentFilter = q.paymentMethod || "";
    },
    isMine(sale) {
      return !!(this.currentUser?.id && sale.userId === this.currentUser.id);
    },
    rowClass(item) {
      if (item.status === "held") return "sales-row-held";
      if (item.status === "pending") return "sales-row-pending";
      return "";
    },
    formatPayments(item) {
      if (item.status === "held" || item.status === "pending") {
        return "Unpaid";
      }
      const methods = item.paymentMethods || [];
      if (!methods.length) return "—";
      return methods.join(", ");
    },
    showHeldOnly() {
      this.statusFilter = "held";
      this.paymentFilter = "";
      this.load();
    },
    recallOnPos(saleId) {
      this.$router.push({ path: "/pos", query: { recall: saleId } });
    },
    mergeCashierOptions(rows) {
      const seen = new Map(
        this.cashierOptions
          .filter((o) => o.value)
          .map((o) => [o.value, o.text]),
      );
      for (const row of rows) {
        if (row.userId && !seen.has(row.userId)) {
          seen.set(row.userId, row.userName || row.cashierName || "Cashier");
        }
      }
      this.cashierOptions = [
        { value: "", text: "All cashiers" },
        ...[...seen.entries()].map(([value, text]) => ({ value, text })),
      ];
    },
    async load() {
      this.loading = true;
      try {
        const params = { storeId: this.selectedStoreId, limit: 200 };
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.canAuditFloor && this.cashierFilter) {
          params.userId = this.cashierFilter;
        }
        if (this.paymentFilter) params.paymentMethod = this.paymentFilter;
        const [sales, held] = await Promise.all([
          ApiService.get("sales", params),
          ApiService.get("sales/held/list", {
            storeId: this.selectedStoreId,
            limit: 50,
          }).catch(() => []),
        ]);
        this.sales = sales;
        this.heldSales = Array.isArray(held) ? held : [];
        this.mergeCashierOptions([...this.sales, ...this.heldSales]);
      } finally {
        this.loading = false;
      }
    },
    canReprint(status) {
      return ["completed", "held", "pending", "voided", "refunded"].includes(
        status,
      );
    },
    reprintTitle(status) {
      if (status === "held" || status === "pending") {
        return "Print unpaid receipt";
      }
      return "Reprint receipt";
    },
    async viewReceipt(saleId) {
      try {
        const receipt = await ApiService.get(`sales/${saleId}/receipt`);
        this.receiptHtml = receipt.htmlContent;
        this.receiptStatus = receipt.sale?.status || "";
        this.showReceipt = true;
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not load receipt",
          icon: "danger",
        });
      }
    },
    async cancelSale(saleId) {
      if (!confirm("Cancel this unpaid sale?")) return;
      try {
        await ApiService.post(`sales/${saleId}/cancel`);
        await this.load();
        toast.fire({
          title: "Sale cancelled",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Cancel failed",
          icon: "danger",
        });
      }
    },
    async voidSale(saleId) {
      const reason = prompt("Reason for void (optional):");
      if (reason === null) return;
      if (
        !confirm(
          "Void this completed sale? Inventory and loyalty will be reversed.",
        )
      )
        return;
      try {
        await ApiService.post(`sales/${saleId}/void`, {
          reason: reason || null,
        });
        await this.load();
        toast.fire({
          title: "Sale voided",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Void failed",
          icon: "danger",
        });
      }
    },
    async refundSale(saleId) {
      const reason = prompt("Reason for refund (optional):");
      if (reason === null) return;
      if (
        !confirm("Refund this sale? Cash should be returned to the customer.")
      )
        return;
      try {
        await ApiService.post(`sales/${saleId}/refund`, {
          reason: reason || null,
        });
        await this.load();
        toast.fire({
          title: "Sale refunded",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Refund failed",
          icon: "danger",
        });
      }
    },
    printReceipt() {
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.write(this.receiptHtml);
      w.document.close();
      w.focus();
      w.print();
    },
  },
};
</script>

<style scoped>
.sales-held-banner {
  padding: 0.75rem 1rem;
  background: #fff8e6;
  border: 1px solid #f5d889;
  border-radius: 8px;
  font-size: 0.9rem;
}
.sales-row-held {
  background-color: #fff8e6 !important;
}
.sales-row-pending {
  background-color: #f8fafc !important;
}
</style>
